/**
 * Modal Field Adapter
 * Transforms Product Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import { Product } from '../../../services/api/domains/products/types';
import { PRODUCT_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { formatDateTime } from '../../../utils/dateConversion';
import { stripHtmlTags } from '../../../utils/renderHtml';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { Facility } from '../../../services/api/domains/facilities/types';
import { Grade } from '../../../services/api/domains/grades/types';
import { TherapeuticArea } from '../../../services/api/domains/therapeutic-areas/types';

/**
 * Options for modal field generation (API-provided data)
 */
export interface ProductFieldOptions {
  manufacturers?: Manufacturer[];
  facilities?: Facility[];
  grades?: Grade[];
  therapeuticAreas?: TherapeuticArea[];
}

/**
 * Convert a basic field definition to modal field config
 */
function createStandardField(field: BaseFieldDefinition): FieldConfig<Product> {
  // Determine field type
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  const config: FieldConfig<Product> = {
    key: field.key as keyof Product,
    label: field.label,
    type: fieldType,
    getValue: (p: Product) => {
      const value = p[field.key as keyof Product];
      if (
        field.renderConfig?.formatAs === 'html' &&
        typeof value === 'string'
      ) {
        return stripHtmlTags(value);
      }
      // Format boolean values for display
      if (field.dataType === 'boolean') {
        if (value === null || value === undefined) return 'Unknown';
        return value ? 'Yes' : 'No';
      }
      return value || '';
    },
    setValue: field.isReadOnly
      ? undefined
      : (p: Product, val: unknown) => ({
          ...p,
          [field.key]: val || undefined,
        }),
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
  };

  // Add formatter for date fields
  if (field.dataType === 'date' && field.isReadOnly) {
    config.formatter = formatDateTime;
  }

  return config;
}

/**
 * Create async multiselect field for relations without status
 * Uses API-based pagination and search instead of pre-loaded data
 */
function createAsyncMultiselectField(
  field: BaseFieldDefinition
): FieldConfig<Product> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { entityName, idField } = relationConfig;

  // Extract async select config from field registry with modal context
  // Modal context uses ID field for value (database storage)
  const contextConfig = getContextAwareConfigFromRegistry(
    PRODUCT_FIELDS_REGISTRY,
    field.key as string,
    'modal'
  );

  if (!contextConfig) {
    throw new Error(
      `Field ${String(field.key)} is missing async select configuration`
    );
  }

  // Convert to standard config for component props
  const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

  return {
    key: field.key as keyof Product,
    label: field.label,
    type: 'async_multiselect',
    getValue: (p: Product) => {
      const items = p[field.key as keyof Product] as unknown as Array<
        Record<string, unknown>
      >;
      if (!Array.isArray(items)) return [];
      return items.map(item => item[idField]?.toString() || '');
    },
    setValue: (p: Product, val: unknown) => {
      const selectedIds = Array.isArray(val) ? val : [];
      const items = selectedIds
        .map(id => {
          // Create minimal objects with just ID and empty name
          // The AsyncMultiSelectField will fetch the full data
          if (entityName === 'grades') {
            return { grade_id: parseInt(id, 10), grade_name: '' };
          } else if (entityName === 'facilities') {
            return { facility_id: parseInt(id, 10), facility_name: '' };
          } else if (entityName === 'therapeutic_areas') {
            return {
              therapeutic_area_id: parseInt(id, 10),
              therapeutic_area_name: '',
            };
          }
          return null;
        })
        .filter(Boolean);

      return { ...p, [field.key]: items };
    },
    apiEndpoint: asyncConfig.apiEndpoint,
    searchField: asyncConfig.searchField,
    valueField: asyncConfig.valueField,
    labelField: asyncConfig.labelField,
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create async multiselect field with status support for manufacturers
 * Currently not used - manufacturers use list_builder instead
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createAsyncMultiselectWithStatusField(
  field: BaseFieldDefinition
): FieldConfig<Product> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { entityName, idField } = relationConfig;

  // Extract async select config from field registry with modal context
  // Modal context uses ID field for value (database storage)
  const contextConfig = getContextAwareConfigFromRegistry(
    PRODUCT_FIELDS_REGISTRY,
    field.key as string,
    'modal'
  );

  if (!contextConfig) {
    throw new Error(
      `Field ${String(field.key)} is missing async select configuration`
    );
  }

  // Convert to standard config for component props
  const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

  return {
    key: field.key as keyof Product,
    label: field.label,
    type: 'async_multiselect_with_status',
    getValue: (p: Product) => {
      const items = p[field.key as keyof Product] as unknown as Array<
        Record<string, unknown>
      >;
      if (!Array.isArray(items)) return [];
      return items.map(item => ({
        id: item[idField]?.toString() || '',
        status: item.manufacturing_status || null,
      }));
    },
    setValue: (p: Product, val: unknown) => {
      const selectedItems = Array.isArray(val) ? val : [];
      const items = selectedItems
        .map(item => {
          if (entityName === 'manufacturers') {
            return {
              manufacturer_id: parseInt(item.id, 10),
              manufacturer_name: '',
              manufacturing_status: item.status || null,
            };
          }
          return null;
        })
        .filter(Boolean);

      return { ...p, [field.key]: items };
    },
    apiEndpoint: asyncConfig.apiEndpoint,
    searchField: asyncConfig.searchField,
    valueField: asyncConfig.valueField,
    labelField: asyncConfig.labelField,
    statusOptions: field.selectionOptions || [],
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create list builder field for relations with status (manufacturers)
 */
function createListBuilderField(
  field: BaseFieldDefinition,
  apiData: ProductFieldOptions
): FieldConfig<Product> {
  const { relationConfig } = field;
  if (!relationConfig) {
    throw new Error(
      `Field ${String(field.key)} is marked as relation but missing relationConfig`
    );
  }

  const { idField, displayField, statusField } = relationConfig;

  // Get available items from API data
  const availableItems =
    apiData.manufacturers?.map(m => ({
      id: m.manufacturer_id,
      label: m.manufacturer_name,
    })) || [];

  return {
    key: field.key as keyof Product,
    label: field.label,
    type: 'list_builder',
    getValue: (p: Product) => {
      const items = p[field.key as keyof Product] as unknown as Array<
        Record<string, unknown>
      >;
      if (!Array.isArray(items)) return [];
      return items.map(item => ({
        id: item[idField],
        label: item[displayField],
        status: statusField ? item[statusField] : null,
      }));
    },
    setValue: (p: Product, val: unknown) => {
      const items = Array.isArray(val)
        ? val.map(item => {
            const manufacturer = apiData.manufacturers?.find(
              m => m.manufacturer_id === item.id
            );
            return {
              manufacturer_id: item.id,
              manufacturer_name: manufacturer?.manufacturer_name || item.label,
              manufacturing_status: item.status || null,
            };
          })
        : [];
      return { ...p, [field.key]: items };
    },
    availableItems,
    listBuilderConfig: {
      idKey: 'Manufacturer',
      labelKey: 'Manufacturer',
      statusKey: 'Status',
      statusOptions: field.selectionOptions || [],
      addButtonText: 'Add Manufacturer',
      addModalTitle: 'Add Manufacturers',
      noItemsText: 'No manufacturers associated with this product',
    },
    isRequired: field.isRequired,
    tooltipText: field.description,
  };
}

/**
 * Create special field for preferred_product_name with synonyms radio
 */
function createPreferredProductNameField(): FieldConfig<Product> {
  return {
    key: 'preferred_product_name',
    label: 'Preferred Product Name',
    type: 'synonyms_radio',
    getValue: (p: Product) => ({
      preferredValue: p.preferred_product_name || '',
      synonyms: p.synonyms || [],
    }),
    setValue: (p: Product, val: unknown) => ({
      ...p,
      preferred_product_name: (val as string) || null,
    }),
    tooltipText:
      'The Product Name is sometimes set as the official CAS Registry name (often a chemical formula). Use Preferred Product Name to set a simpler, common name for internal reference. Select from synonyms below or enter a custom name.',
  };
}

/**
 * Convert Product Field Registry to Modal field configurations
 * @param apiData - Data from API calls (manufacturers, facilities, etc.)
 * @returns Array of FieldConfig for Modal
 */
export function toModalFields(
  apiData: ProductFieldOptions = {}
): FieldConfig<Product>[] {
  const fields: FieldConfig<Product>[] = [];

  for (const field of PRODUCT_FIELDS_REGISTRY) {
    // Skip fields not relevant for modal editing
    if (field.key === 'synonyms' || field.key === 'replaced_rns') {
      continue; // These are handled differently or not editable
    }

    // Handle preferred_product_name specially
    if (field.key === 'preferred_product_name') {
      fields.push(createPreferredProductNameField());
      continue;
    }

    // Handle relational fields
    if (field.isRelation && field.relationConfig) {
      const hasStatus = !!field.relationConfig.statusField;
      if (hasStatus) {
        // Use list builder for manufacturers (requires pre-loaded data with status)
        fields.push(createListBuilderField(field, apiData));
      } else {
        fields.push(createAsyncMultiselectField(field));
      }
      continue;
    }

    // Handle standard fields
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get always disabled field names for products
 */
export function getAlwaysDisabledFields(): string[] {
  return PRODUCT_FIELDS_REGISTRY.filter(f => f.isReadOnly).map(
    f => f.key as string
  );
}
